import { parseDateToGivenFormat } from '@/utils/dateUtils';
import { readValueRule } from '@/formConfigs/fieldValueRule';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';

const { currencySupportsDecimal, normalizeCurrencyDecimals } =
  currencyFormatterHelpers;

const REJECTED_OFFER_STATUSES = ['rejected', 'rejected_by_candidate'];

// Currency display string off a BE currency object (either shape).
const currencyStrOf = (c: any): string | undefined =>
  c?.currency || c?.currency_code;

// Whether a BE currency object represents a comma-separator (dot-decimal)
// currency, i.e. one where decimals should be retained.
const currencySupportsDecimalFor = (c: any): boolean => {
  const str = currencyStrOf(c);
  return !!str && currencySupportsDecimal(str);
};

// Convert a backend default to a number, but leave the field unset when the
// value is null/undefined/empty. `Number(null)` is 0 and `Number(undefined)`
// is NaN — both leak into the form and render as "0"/"NaN" in the inputs.
const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

// rel_user ("Users") values come back from the API as user cards; file values
// carry url/filename and must not match (mirrors requisitionFormHelpers).
const isUserCardArray = (value: unknown): value is Array<{ id: number }> =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (item) =>
      !!item &&
      typeof item === 'object' &&
      'id' in item &&
      !('url' in item) &&
      !('filename' in item)
  );

/**
 * Attach the avatar option renderer to every rel_user ("Users") field config.
 * The renderer is a component, so it's attached at the page layer rather than
 * inside generateCustomFormConfig — mirrors useCreateRequisitions. `fields`
 * are the (remapped) custom-field rows the config was generated from; their
 * kind may live in `kind` (cf records) or `type` (form-template rows).
 */
export const attachRelUserAvatarRenderer = (
  fields: any[] = [],
  fieldConfigs: any[] = [],
  avatarRenderer: unknown
): any[] => {
  const relUserFieldNames = new Set(
    fields
      .filter((field) => (field?.kind ?? field?.type) === 'rel_user')
      .map((field) => `custom_${field?.id}`)
  );

  return fieldConfigs.map((fieldConfig) =>
    relUserFieldNames.has(fieldConfig?.name)
      ? { ...fieldConfig, customFormatLabel: avatarRenderer }
      : fieldConfig
  );
};

// Labels of required custom fields whose Formik value is blank — preflight before
// submit to catch CF widgets that never registered with Formik (renderer race) so
// Yup passed but BE would reject with err_processing_form_data.
export const getMissingRequiredCustomFields = (
  atsFormFields: any[] = [],
  values: Record<string, any> = {}
): string[] => {
  const isBlank = (v: any) =>
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
  // A single-select dropdown's "unselected" state serialises to the sentinel
  // 0 / "0" (real option ids are always positive), which isBlank reads as
  // filled — so an empty required dropdown slipped past this preflight and 422'd
  // on the backend. Treat the sentinel as blank for single-select dropdowns
  // only, so a legitimate 0 in a required number field is untouched. (ENG-3456)
  const isEmptyDropdown = (f: any, v: any) =>
    f?.kind === 'rel_tag' && !f?.multi && (v === 0 || v === '0');
  const missing: string[] = [];
  (atsFormFields || []).forEach((f) => {
    const isCustom =
      f?.field_kind === 'custom' ||
      (f?.ats_custom_field_id !== null && f?.ats_custom_field_id !== undefined);
    // Hidden fields are filtered out of the form (see applyFieldDetails), so the
    // user can never fill them — flagging one here would hard-block offer
    // creation with a toast they cannot action. A required + hidden field is a
    // template misconfiguration to resolve on the backend, not a user error, so
    // skip it rather than perma-block the offer. (ENG-3456)
    if (!isCustom || !f?.required || f?.hidden) return;
    const cfId = f?.ats_custom_field_id ?? f?.id;
    const rowId = f?.id;
    const key1 = `custom_${cfId}`;
    const key2 = `custom_${rowId}`;
    const v = values?.[key1] ?? values?.[key2];
    if (isBlank(v) || isEmptyDropdown(f, v)) {
      missing.push(f?.label || `custom_${cfId}`);
    }
  });
  return missing;
};

// Opaque backend error codes → user-facing copy for the offer submit toasts.
const OFFER_ERROR_MESSAGES: Record<string, string> = {
  err_processing_form_data:
    'Please fill all required fields before submitting the offer.',
  // Custom-field validation now reports a specific code per failure mode instead
  // of the single opaque err_processing_form_data (kula-ai/core #5972), so each
  // one needs copy or the recruiter sees the raw code in the toast.
  err_invalid_value:
    "One of the fields has a value we can't accept. Check it and try again.",
  err_value_out_of_range: 'A number is too large for its field.',
  err_value_too_long: 'One of the text fields is too long.',
  err_invalid_date: 'Check the date you entered.',
  err_invalid_url: 'Check the link you entered.',
  err_invalid_file: "We couldn't attach that file. Try uploading it again.",
  err_invalid_rating: 'Check the rating you entered.',
  err_rating_out_of_range: 'A rating is outside the allowed range.',
  err_invalid_rel_tag: 'Check the tag you selected.',
  err_invalid_rel_user: 'Check the person you selected.',
  err_approval_reviewer_field_empty:
    'Select a reviewer before submitting the offer.',
  err_formula_field_inaccessible:
    'A calculated field reads a field this offer cannot see. Ask an admin to check the offer form.',
};

// Pull the most user-meaningful string out of an offer API error — whatever
// shape the base query hands back (array of codes/messages, `{ errors: [...] }`,
// `{ error }`, or a bare string) — and map known opaque codes to human copy.
// Falls back to the raw first value, then a generic message, so the toast never
// regresses to an empty/undefined description. (ENG-3456)
export const getOfferErrorMessage = (
  error: any,
  fallback = 'Something went wrong. Please try again.'
): string => {
  const first = Array.isArray(error)
    ? error[0]
    : Array.isArray(error?.errors)
      ? error.errors[0]
      : (error?.error ?? error?.message ?? error);
  const code =
    typeof first === 'string' ? first : (first?.err_code ?? first?.msg);
  if (typeof code === 'string' && OFFER_ERROR_MESSAGES[code]) {
    return OFFER_ERROR_MESSAGES[code];
  }
  // A code we haven't mapped is not copy — showing it is how `err_invalid_value`
  // reached a recruiter's toast. A readable sentence from the backend still
  // passes through; anything shaped like a code falls back to the generic line.
  const isUnmappedCode =
    typeof code === 'string' && /^err_[a-z0-9_]+$/.test(code);
  if (isUnmappedCode) return fallback;
  return typeof code === 'string' && code ? code : fallback;
};

// Map each custom field's cf-id to its ats_form_field row id. Formik keys custom
// fields by cf-id post CORE-4689, but BE's form_data_handler keys the required
// check by row id — generateOfferPayload uses this map to translate the keys back
// before the PUT, otherwise required CFs 422 with err_processing_form_data.
export const buildCfIdToRowIdMap = (
  atsFormFields: any[] = []
): Record<string, string> =>
  (atsFormFields || []).reduce(
    (acc: Record<string, string>, f: any) => {
      if (
        f?.ats_custom_field_id !== null &&
        f?.ats_custom_field_id !== undefined &&
        f?.id !== null &&
        f?.id !== undefined
      ) {
        acc[String(f.ats_custom_field_id)] = String(f.id);
      }
      return acc;
    },
    {} as Record<string, string>
  );

interface AdditionalCompensationDefaults {
  amount?: string | null;
  strike_price?: string;
  unit_of_cliff?: string;
  type_of_equity?: string;
  cliff_of_vesting?: string;
  period_of_vesting?: string;
  cycles_after_cliff?: string;
  // An object, not a string: Settings -> Offer fields writes
  // `{ id, currency }` (OfferFieldExtras) and this reads `.id` off it. The
  // `string` here was wrong and produced a TS2339 on that read.
  strike_price_currency?: { id?: number; currency?: string } | null;
  unit_of_vesting_period?: string;
  unit_of_cycle_after_cliff?: string;
}

// The application GET returns a composed `name`; the job-candidates list row returns
// first_name/last_name. Accept either so the offer drawer prefills the candidate
// whichever surface opened it (ENG-3696).
export const getContactName = (contactData?: any): string =>
  contactData?.name ||
  [contactData?.first_name, contactData?.last_name].filter(Boolean).join(' ');

// The defaults offer-field settings configured, keyed the way the form names
// its custom fields. A calculated field is left out — it works its own value
// out — and so is a blank default, which is the same as having none.
export const configuredFieldDefaults = (
  formFields: any[] = [],
  // Locked fields are collected separately: the backend imposes their value on
  // every save, so what the offer happens to hold is already out of date.
  { locked = false }: { locked?: boolean } = {}
) =>
  (formFields || []).reduce((acc: Record<string, any>, field: any) => {
    const cfId = field?.ats_custom_field_id;
    if (cfId === null || cfId === undefined) return acc;
    const rule = readValueRule(field);
    const { default_value, formula } = rule;
    if (formula) return acc;
    if (Boolean(rule.locked) !== locked) return acc;
    if (default_value === null || default_value === undefined) return acc;
    if (default_value === '') return acc;
    if (Array.isArray(default_value) && default_value.length === 0) return acc;
    acc[`custom_${cfId}`] = default_value;
    return acc;
  }, {});

export const getInitializeOfferValues = ({
  contactData,
  currentOffer,
  currentJob,
  currUser,
  type,
  additionalCompensationDefaults = {},
  rowIdToCfIdMap = {},
  applyStructuralDefaults = true,
  defaultCurrencyId,
  formFields = [],
}: {
  contactData?: any;
  currentOffer?: any;
  currentJob?: any;
  currUser?: any;
  type?: 'create' | 'edit';
  additionalCompensationDefaults?: AdditionalCompensationDefaults;
  rowIdToCfIdMap?: Record<string, string>;
  // When false, omit the hardcoded structural fallbacks (currency ids → 5,
  // vesting units → year/month) and return only values the offer actually has.
  // Used to build a fill-source for send-mode token validation so a field that
  // exists ONLY as an FE default doesn't mask a new template's required token
  // (CORE-4822). Defaults true so create/edit callers are unchanged.
  applyStructuralDefaults?: boolean;
  // Currency to prefill on every money field the offer carries none for —
  // salary, additional bonus, equity money and strike price — resolved from the
  // job's office by `resolveOfferDefaultCurrencyId` (CORE-4740).
  //
  // There is deliberately NO fallback beneath it: when the job's office
  // configures no currency the field opens BLANK so the recruiter picks one. A
  // confident-but-wrong USD is what gets silently accepted, and that is the bug
  // this replaces. A currency the offer was saved with, or one seeded from
  // offer-field settings, still wins.
  defaultCurrencyId?: number;
  // The offer's form fields, which carry the default each was configured with.
  formFields?: any[];
}) => {
  const name = getContactName(contactData);
  const { email } = contactData || {};
  const { basic_info, name: jobName } = currentJob || {};

  const {
    candidate_name,
    candidate_email,
    candidate_phone = '',
    candidate_address = '',
    salary_currency,
    salary = '',
    ats_equity_award,
    reporting_manager,
    department,
    office,
    additional_compensation = '',
    additional_compensation_currency,
    ats_offer_template,
    additional_info,
    today_date,
    start_date,
    expiration_date,
    salary_frequency,
    job_title,
    req_id,
  } = currentOffer || {};

  const {
    strike_price_currency,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    amount = '',
    strike_price,
    currency,
  } = ats_equity_award || {};
  const ALLOWED_REQUISITION_STATUSES = ['approved', 'created', 'open'];
  const { basic_info: { requisitions = [] } = {} } = currentJob || {};
  const userHasReqEnabled = currUser?.requisition?.enabled;

  const filteredRequisitions =
    requisitions?.filter(
      (req) =>
        ALLOWED_REQUISITION_STATUSES.includes(req?.status) &&
        !req?.has_association
    ) || [];

  const isRejectedWithNoLinkedReq =
    currentOffer?.id &&
    !currentOffer?.requisition?.id &&
    REJECTED_OFFER_STATUSES.includes(currentOffer?.offer_status) &&
    userHasReqEnabled;

  const JobAssociatedRequisition = isRejectedWithNoLinkedReq
    ? undefined
    : userHasReqEnabled
      ? filteredRequisitions[0]?.code
      : basic_info?.requisitions?.[0]?.requisition_value;

  // Retain decimals on reopen only for comma-separator currencies (full-stop
  // currencies and no-currency stay integer-only). Truncating on hydration
  // would silently overwrite the BE decimal on the next save (ENG-3539).
  const keepSalaryDecimals = currencySupportsDecimalFor(salary_currency);
  const keepAdditionalCompDecimals = currencySupportsDecimalFor(
    additional_compensation_currency
  );
  const keepStrikeDecimals = currencySupportsDecimalFor(strike_price_currency);
  // Equity units carry no currency (a plain count) and render their raw value,
  // so keep their decimals to stop reopen+resave silently truncating the BE
  // (e.g. 22.5 -> 22). Equity money is a number-mode currency field that floors
  // to an integer on display, so it stays integer-only (unchanged).
  const keepAmountDecimals = !currency?.id;

  // Apply additional compensation defaults only when type is 'create'
  const createTypeDefaults =
    type === 'create'
      ? {
          unit_of_cliff: additionalCompensationDefaults.unit_of_cliff || 'year',
          unit_of_cycle_after_cliff:
            additionalCompensationDefaults.unit_of_cycle_after_cliff || 'month',
          unit_of_vesting_period:
            additionalCompensationDefaults.unit_of_vesting_period || 'year',
          cliff_of_vesting: toNumberOrUndefined(
            additionalCompensationDefaults.cliff_of_vesting
          ),
          period_of_vesting: toNumberOrUndefined(
            additionalCompensationDefaults.period_of_vesting
          ),
          cycles_after_cliff: toNumberOrUndefined(
            additionalCompensationDefaults.cycles_after_cliff
          ),
          strike_price: toNumberOrUndefined(
            additionalCompensationDefaults.strike_price
          ),
          type_of_equity: additionalCompensationDefaults.type_of_equity,
          ...(additionalCompensationDefaults.amount !== null &&
            additionalCompensationDefaults.amount !== undefined && {
              amount: Number(additionalCompensationDefaults.amount),
            }),
          // Settings -> Offer fields can seed an explicit strike-price currency;
          // an admin's pick beats an office inference, so it stays at the front.
          strike_price_currency_id:
            Number(additionalCompensationDefaults.strike_price_currency?.id) ||
            Number(strike_price_currency?.id) ||
            defaultCurrencyId ||
            undefined,
        }
      : {};

  const defaults = {
    job_title: job_title || jobName,
    candidate_name: candidate_name || name,
    candidate_email: candidate_email || email,
    candidate_phone,
    candidate_address,
    salary_currency_id: applyStructuralDefaults
      ? (salary_currency?.id ?? defaultCurrencyId)
      : salary_currency?.id,
    strike_price_currency_id: applyStructuralDefaults
      ? (strike_price_currency?.id ?? defaultCurrencyId)
      : strike_price_currency?.id,
    today_date: today_date,
    ats_department_id: department?.id || basic_info?.department?.id,
    ats_office_id: office?.id || basic_info?.office?.[0]?.id,
    additional_compensation_currency_id: applyStructuralDefaults
      ? (additional_compensation_currency?.id ?? defaultCurrencyId)
      : additional_compensation_currency?.id,
    reporting_manager_id: reporting_manager?.id,
    unit_of_cliff: applyStructuralDefaults
      ? unit_of_cliff || 'year'
      : unit_of_cliff,
    unit_of_cycle_after_cliff: applyStructuralDefaults
      ? unit_of_cycle_after_cliff || 'month'
      : unit_of_cycle_after_cliff,
    unit_of_vesting_period: applyStructuralDefaults
      ? unit_of_vesting_period || 'year'
      : unit_of_vesting_period,
    award_type: currency?.id ? 'money' : 'units',
    // Equity money only. Units mode sends `currency_id: null` from
    // generateOfferPayload — a null currency is what *means* units.
    currency_id: applyStructuralDefaults
      ? (currency?.id ?? defaultCurrencyId)
      : currency?.id,
    salary: keepSalaryDecimals
      ? normalizeCurrencyDecimals(salary)
      : salary?.split('.')?.[0],
    ...(amount !== null &&
      amount !== undefined && {
        amount: keepAmountDecimals
          ? normalizeCurrencyDecimals(amount)
          : amount?.split('.')?.[0],
      }),
    ...(strike_price !== null &&
      strike_price !== undefined && {
        strike_price: keepStrikeDecimals
          ? normalizeCurrencyDecimals(strike_price)
          : strike_price?.toString()?.split('.')?.[0],
      }),
    additional_compensation: keepAdditionalCompDecimals
      ? normalizeCurrencyDecimals(additional_compensation)
      : additional_compensation?.split('.')?.[0],
    start_date,
    expiration_date,
    salary_frequency,
    req_id: isRejectedWithNoLinkedReq
      ? undefined
      : req_id || JobAssociatedRequisition,
    requisitions_codes: isRejectedWithNoLinkedReq
      ? undefined
      : currentOffer?.requisition?.code ||
        (userHasReqEnabled ? filteredRequisitions[0]?.code : undefined) ||
        req_id,
    ...createTypeDefaults,
  };

  const details = {
    ...ats_equity_award,
    ...defaults,
    // What settings configured this field to start at. It sits above the
    // offer's own values so anything already filled in — including a field
    // deliberately cleared, which comes back explicitly null — still wins.
    ...configuredFieldDefaults(formFields),
    //adding custom fields default value
    ...additional_info?.reduce((acc, { field_id, value, ext_data }) => {
      // Prefer ext_data when BE preserves the verbatim user string (added in
      // core PRs 4861/4864 for number fields). The Decimal-typed `value` would
      // otherwise truncate EU thousand-separator inputs like "1.234.567" to
      // 1.234 on reopen.
      const mappedKey = rowIdToCfIdMap[String(field_id)] || String(field_id);
      const seeded = ext_data ?? value;
      // rel_user ("Users"): API returns user cards [{ id, name, avatar }]; the
      // picker binds to user ids, so map the cards down to their ids (mirrors
      // requisitionFormHelpers).
      acc[`custom_${mappedKey}`] = isUserCardArray(seeded)
        ? seeded.map((user) => user?.id)
        : seeded;
      return acc;
    }, {}),
    // A locked field is the exception: the backend replaces whatever the offer
    // stored with the admin's value on the next save, so showing the old one
    // under a padlock would be a number the recruiter can neither change nor
    // rely on.
    ...configuredFieldDefaults(formFields, { locked: true }),
  };

  return {
    details,
    templates: {
      ats_offer_template_id: ats_offer_template?.id,
      ats_offer_template: ats_offer_template || {},
    },
  };
};

// File custom fields must go to the backend as a signed blob id (string) or an
// existing attachment id (integer) — never the raw file object. New uploads are
// already converted to signed_id strings before this runs (via
// processFormFilesForUpload at the submit site); here we only unwrap EXISTING
// file objects (which come back from the API carrying `signed_id`/`id` +
// `url`/`filename`). This mirrors how requisitions builds `additional_info`.
// Genuine multi-value arrays (e.g. multi-selects) are left untouched.
const isExistingFileObject = (item: any): boolean =>
  !!item &&
  typeof item === 'object' &&
  ('signed_id' in item || 'id' in item) &&
  ('url' in item || 'filename' in item);

const normalizeCustomFieldValue = (value: any) => {
  if (isExistingFileObject(value)) {
    return value?.signed_id || value?.id || null;
  }
  if (!Array.isArray(value)) return value;
  if (!value.some(isExistingFileObject)) return value;

  const fileIds = value
    .map((item) =>
      typeof item === 'string' ? item : item?.signed_id || item?.id
    )
    .filter(Boolean);

  if (fileIds.length === 0) return undefined;
  return fileIds.length === 1 ? fileIds[0] : fileIds;
};

function generateCustomPayload(
  obj,
  cfIdToRowIdMap: Record<string, string> = {}
) {
  const customObject = {};
  const payload = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('custom_')) {
      const cfKey = key.replace('custom_', '');
      // Translate cf-id keys (post CORE-4689 remap) back to the ats_form_field row
      // id BE expects; fall back to the key as-is for legacy/flag-off paths.
      const newKey = cfIdToRowIdMap[cfKey] || cfKey;
      const normalized = normalizeCustomFieldValue(value);
      if (normalized !== undefined) {
        customObject[newKey] = normalized;
      }
    } else {
      payload[key] = value;
    }
  }

  return {
    customObject,
    payload,
  };
}

export const generateOfferPayload = ({
  values,
  applicationId,
  offerId: _offerId = 0,
  cfIdToRowIdMap = {},
}: {
  values: any;
  applicationId?: any;
  offerId?: any;
  cfIdToRowIdMap?: Record<string, string>;
}) => {
  const {
    cliff_of_vesting,
    cycles_after_cliff,
    date_of_grant,
    period_of_vesting,
    start_date_of_vesting,
    strike_price,
    type_of_equity,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    amount,
    currency_id,
    award_type,
    today_date,
    start_date,
    expiration_date,
    strike_price_currency_id,
    useLegacy,
    ...otherValues
  } = values || {};

  const { customObject = {}, payload = {} } = generateCustomPayload(
    otherValues,
    cfIdToRowIdMap
  );

  if (Object.keys(customObject)?.length > 0) {
    payload['additional_info'] = customObject;
  }

  if (useLegacy !== undefined) {
    payload['use_legacy_flow'] = useLegacy;
  }

  // @ts-expect-error payload is loosely typed
  const { currency: _currency, ...validPayloads } = payload || {};
  const ats_equity_award_attributes = {
    amount,
    cliff_of_vesting,
    cycles_after_cliff,
    date_of_grant,
    period_of_vesting,
    start_date_of_vesting,
    strike_price,
    type_of_equity,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    strike_price_currency_id,
    currency_id: award_type === 'money' ? currency_id : null,
  };

  return {
    ats_offer: {
      ats_application_id: applicationId,
      ats_equity_award_attributes,
      today_date:
        today_date && parseDateToGivenFormat(today_date, 'YYYY-MM-DD'),
      start_date:
        start_date && parseDateToGivenFormat(start_date, 'YYYY-MM-DD'),
      expiration_date:
        expiration_date &&
        parseDateToGivenFormat(expiration_date, 'YYYY-MM-DD'),
      ...validPayloads,
      // @ts-expect-error validPayloads is loosely typed
      req_id: validPayloads.requisitions_codes
        ? Array.isArray((validPayloads as any).requisitions_codes)
          ? (validPayloads as any).requisitions_codes[0]
          : (validPayloads as any).requisitions_codes
        : (validPayloads as any).req_id,
    },
  };
};

export const pollOfferWithRetries = async (
  pollOffer,
  pollId,
  // ~90s window so multi-doc offers don't hit a premature FE timeout on slow
  // generation (CORE-4795).
  maxRetries = 30,
  timeout = 3000
) => {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await pollOffer({ id: pollId });
      if (response.data) {
        const status = response.data.offer_status;
        if (status === 'document_generated' || status === 'approved_internal') {
          return response.data;
        } else if (status === 'document_generation_error') {
          throw new Error('Document generation error');
        } else if (status !== 'pending_document_generation') {
          throw new Error('Unexpected status');
        }
      }
    } catch (err) {
      if (
        err.message === 'Document generation error' ||
        retries === maxRetries - 1
      ) {
        throw err;
      }
    }
    retries += 1;
    await new Promise((res) => setTimeout(res, timeout));
  }
  throw new Error('Maximum retries reached');
};

// Tokens derived from the selected office at render time ({{offer.office_city}}
// etc. resolve via AtsOfferDrop) — no offer-form field carries them, so the
// fill-check treats them like {{offer.office}}: an office selection satisfies them.
const OFFICE_DERIVED_TOKENS = [
  'offer.office_city',
  'offer.office_state',
  'offer.office_country',
];

export const extractTokenArrays = ({ templateTokens, filledValues }) => {
  // Extract valid and invalid tokens from the template
  const validTokens = Object.keys(templateTokens).filter(
    (key) =>
      templateTokens[key] === 'valid' &&
      (key.startsWith('offer.') || key.startsWith('offer_cf.'))
  );

  const invalidTokens = Object.keys(templateTokens).filter(
    (key) =>
      templateTokens[key] === 'invalid' &&
      (key.startsWith('offer.') || key.startsWith('offer_cf.'))
  );

  // Extract keys in filledValues that are not in templateTokens but have a value
  const filledTokensNotInTemplate = Object.keys(filledValues || {}).filter(
    (key) => {
      return (
        !Object.prototype.hasOwnProperty.call(templateTokens, key) &&
        !key.startsWith('docusign') &&
        !key.startsWith('pandadocs') &&
        !key.startsWith('adobe') &&
        !OFFICE_DERIVED_TOKENS.includes(key) &&
        filledValues[key] !== null &&
        filledValues[key] !== undefined &&
        filledValues[key] !== ''
      );
    }
  );

  // Extract non-filled valid tokens (valid in template but not present in filledValues or having empty/null/undefined value)
  const nonFilledValidTokens = validTokens.filter((key) => {
    // Offer section tokens ({{offer.<field>_section}}) resolve on the backend to
    // the selected option's content — or to nothing when the option has no
    // section — so they're never a "missing field" and have no filledValues entry.
    if (key.endsWith('_section')) return false;
    return (
      !Object.prototype.hasOwnProperty.call(filledValues, key) ||
      filledValues[key] === null ||
      filledValues[key] === undefined ||
      filledValues[key] === ''
    );
  });

  return {
    validTokens,
    invalidTokens,
    filledTokensNotInTemplate,
    nonFilledValidTokens,
  };
};

export const getFilledValues = ({
  detailsFormConfig,
  detailsFormRef,
  offerPayload = null,
  overrideValues = null,
}: {
  detailsFormConfig: any[];
  detailsFormRef?: any;
  offerPayload?: Record<string, any> | null;
  // Explicit fill-source that bypasses the live form ref / offerPayload. Send mode
  // passes the offer's raw values (no FE structural defaults) merged with the user's
  // inline saves, so a field that exists only as a default doesn't read as filled
  // and mask a new template's required token (CORE-4822).
  overrideValues?: Record<string, any> | null;
}) => {
  const filledValues = {};

  // `|| undefined` would treat 0 / false / '' as "not filled" and surface the
  // "fields not filled for tokens" warning even though the user did enter 0
  // — keep only true emptiness (null / undefined / '') as missing.
  const pickIfFilled = (value: unknown) =>
    value !== '' && value !== null && value !== undefined ? value : undefined;

  // When step 1 unmounts, Formik may leave the ref as a stale object with empty
  // values rather than nulling it — `??` would not catch that. Prefer offerPayload
  // (set by OfferDetails on mount + every Next click) when it has data; otherwise
  // fall back to the live form ref (user is still on step 1).
  const liveValues = detailsFormRef?.current?.values;
  const hasLiveValues =
    liveValues !== null &&
    liveValues !== undefined &&
    Object.keys(liveValues).length > 0;
  // An explicit override wins: send mode passes the raw offer values + inline saves
  // and must NOT read the hidden OfferDetails ref (which carries FE defaults).
  const formValues = overrideValues
    ? overrideValues
    : hasLiveValues
      ? liveValues
      : offerPayload || {};

  // For custom fields we additionally treat empty arrays / empty objects as
  // missing. Use the actual value (not `value || []`) for the type check so
  // that 0 / false don't fall back to a collection literal and get flagged.
  const isFilledCustomValue = (value: unknown) => {
    if (value === '' || value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    // A Date has no own enumerable keys, so the Object.keys check below reads it as
    // empty — treat it as filled so a custom date isn't re-prompted (CORE-4822).
    if (value instanceof Date) return true;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  // Iterate all sections rather than looking up hardcoded IDs — under
  // feOfferFieldReorder the BE controls which fields land in which section,
  // so a hardcoded allow-list silently drops any field not in a recognised
  // section (e.g. candidate_email moved to a different section).
  (detailsFormConfig || []).forEach((section) => {
    (section?.fields || []).forEach((field) => {
      if (!field?.tokenName) return;
      const value = formValues?.[field.name];
      // Decide custom-vs-static per field, not per section: only offer_cf.* fields
      // use the array/object emptiness rules. A static field BE parked in the custom
      // section (e.g. start_date) must use the plain check, else the Date/scalar
      // emptiness rule reads it as "not filled".
      const isCustomField = field.tokenName.startsWith('offer_cf.');
      filledValues[field.tokenName] = isCustomField
        ? isFilledCustomValue(value)
          ? value
          : undefined
        : pickIfFilled(value);
    });
  });

  // Requisition-enabled accounts capture the Req ID through the requisition
  // selector (`requisitions_codes`), so no plain config field carries the
  // `offer.req_id` token — leaving it perpetually flagged "not filled" on
  // re-validation even though the offer has a Req ID. Backfill the token from
  // whichever req source the form holds (mirrors AssociateReq's own resolution).
  if (filledValues['offer.req_id'] === undefined) {
    const reqSource =
      formValues?.requisitions_codes ||
      formValues?.req_id ||
      formValues?.associate_req_id;
    const reqId = pickIfFilled(
      Array.isArray(reqSource) ? reqSource[0] : reqSource
    );
    if (reqId !== undefined) {
      filledValues['offer.req_id'] = reqId;
    }
  }

  // Office-derived tokens auto-populate from the selected office when the
  // document renders — the recruiter cannot fill them here, so a selected
  // office marks them filled (same signal that fills {{offer.office}}).
  if (filledValues['offer.office'] !== undefined) {
    OFFICE_DERIVED_TOKENS.forEach((token) => {
      if (filledValues[token] === undefined) {
        filledValues[token] = filledValues['offer.office'];
      }
    });
  }

  return filledValues;
};

export const getButtonDisabled = ({
  tabIndex,
  detailsFormRef,
  templatesProps,
  detailsFormConfig,
  isSkipDoc = false,
}) => {
  if (tabIndex === 0) return false;
  if (tabIndex === 1) {
    if (!templatesProps?.selected_template_id) {
      return !isSkipDoc;
    }
    const { invalidTokens, nonFilledValidTokens } = extractTokenArrays({
      templateTokens: getUpdatedTokens(templatesProps?.template_value?.tokens),
      filledValues: getFilledValues({ detailsFormConfig, detailsFormRef }),
    });
    if (invalidTokens?.length > 0) return true;
    if (nonFilledValidTokens?.length > 0) return true;
    return false;
  }
};

export const getUpdatedTokens = (tokens) => {
  // Maps old template token names to the current FE tokenName so templates
  // created before a variable rename still match filledValues keys.
  const mapping = {
    'offer.today_date': 'offer.offer_date',
    'offer.salary_frequency': 'offer.pay_type',
    'offer.additional_compensation': 'offer.additional_bonus',
    'offer.equity_cliff_of_vesting': 'offer.equity_cliff',
    'offer.equity_cycles_after_cliff': 'offer.equity_post_cliff_vesting_cycle',
  };

  const updatedTokens = {};

  for (const key in tokens) {
    // Rename old token names to current names; pass everything else through.
    // Previously excluded keys that appeared as mapping VALUES, which caused
    // templates already using the current short names to have those tokens
    // silently deleted and flagged as "filled but not in template".
    updatedTokens[mapping[key] ?? key] = tokens[key];
  }

  return updatedTokens;
};

// Decide create vs update for an offer-details save. Guards on offerId (the caller passes the
// latest value read straight from the store) in addition to isOfferCreated: after "Save draft"
// creates the offer, the memoized "Skip offer letter generation" handler holds a stale
// isOfferCreated and would otherwise re-POST create -> err_ats_offer_already_created (CORE-4679).
export const shouldCreateOffer = ({
  type,
  isOfferCreated,
  offerId,
}: {
  type: string;
  isOfferCreated: boolean;
  offerId?: number | string | null;
}) => type === 'create' && !isOfferCreated && !offerId;

// Resolve a static field's offer-token variable (e.g. "offer.start_date"): try the
// FE group first, then fall back to any section — BE can relocate a static field to
// a different section, and a group-only lookup would miss it and leave the token
// reading "not filled".
export const resolveStaticFieldVariable = (
  offerStaticFields: any[],
  groupId: string,
  fieldId: string
): string => {
  const inGroup = offerStaticFields
    ?.find((section) => section?.id === groupId)
    ?.fields?.find((field) => field?.id === fieldId)?.variable;
  const variable =
    inGroup ||
    offerStaticFields
      ?.flatMap((section) => section?.fields || [])
      ?.find((field) => field?.id === fieldId)?.variable;
  return variable ? `offer.${variable}` : '';
};
